import { SCRIBE_STATE_NAMES } from 'applications/scribe/instrumentations/track';
import cx from 'classnames';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import ScalableImg from 'common/scalable-img';
import usePrevious from 'common/usePrevious';
import { ServiceConfigurationField } from 'components/v2/anytime-booking/common';
import SelectField from 'components/v2/select-field/select-field';
import TaxiTypeTnc from 'components/v2/taxi-type-tnc/anytime-booking';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import NewBookingDuck from 'ducks/new-booking-duck';
import { Col, Icon, Row } from 'grab-ui';
import { mergeDisplayError } from 'helper/anytime-booking';
import I18n from 'i18n-js';
import _findIndex from 'lodash/findIndex';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import WarningRedIcon from 'public/icons/warning-red.svg';
import React, { useCallback, useEffect, useMemo } from 'react';
import { recommendedVehicleTypeSelector } from 'selectors/new-booking-selectors';
import { deliveryFormFields } from './common';
import styles from './styles.less';

const NO_VEHICLE_SELECTED_VALUE = '';

const VehicleTypeField = ({ form, delivery, disabled = false }) => {
  const recommendedVehicleType = recommendedVehicleTypeSelector(delivery);
  const vehicleType = _get(delivery, 'detail.configuration.vehicleType');
  const vehicleTypes = _get(delivery, 'config.vehicleTypes', []);
  const quote = _get(delivery, 'quote');

  // check if quote returned
  const shouldDisableAllOptions = useMemo(() => !quote, [quote]);

  const noVehicleOption = useMemo(
    () => ({
      value: NO_VEHICLE_SELECTED_VALUE,
      disabled: true,
      child: (
        <div className={styles.vehicleTypeNoneOption}>
          <ScalableImg className={styles.icon} src={WarningRedIcon} alt="" />
          <div className={styles.vehicleType}>
            <span className={styles.name}>
              {I18n.t('v2.anytimeBooking.deliveryForm.noVehicleSelected')}
            </span>
          </div>
        </div>
      ),
    }),
    [],
  );

  const selectOptions = useMemo(
    () =>
      vehicleTypes
        .map(vehicleType => {
          const icon = vehicleType.icon;
          const errorMessage = _get(vehicleType, 'errorMessage');
          const { reason } = mergeDisplayError(errorMessage);
          return {
            value: vehicleType.id,
            disabled: shouldDisableAllOptions || !!errorMessage,
            child: (
              <div className={styles.vehicleTypeOption}>
                {icon && (
                  <ScalableImg className={styles.icon} src={icon} alt="" />
                )}
                <div className={styles.vehicleType}>
                  <span className={styles.name}>{vehicleType.name}</span>
                  <span
                    className={cx(styles.description, {
                      errorDescription: !!errorMessage,
                    })}
                  >
                    {reason || vehicleType.description}
                  </span>
                </div>
              </div>
            ),
          };
        })
        .sort((a, b) => a.value.localeCompare(b.value)),
    [vehicleTypes, shouldDisableAllOptions],
  );

  const options = useMemo(
    () =>
      shouldDisableAllOptions
        ? [noVehicleOption].concat(selectOptions)
        : selectOptions,
    [noVehicleOption, selectOptions, shouldDisableAllOptions],
  );

  const previousVehicleTypeInForm = usePrevious(
    form.getFieldValue(deliveryFormFields.vehicleType.fieldName),
  );

  const selectedValue = useMemo(
    () => {
      if (!quote) return NO_VEHICLE_SELECTED_VALUE;
      if (!vehicleType) return recommendedVehicleType;
      const shouldUseRecommendedVehicleType =
        _findIndex(options, x => x.value === vehicleType && !!x.disabled) !==
        -1;
      return shouldUseRecommendedVehicleType
        ? recommendedVehicleType
        : vehicleType;
    },
    [quote, options, vehicleType, recommendedVehicleType],
  );

  const fieldOptions = useMemo(
    () => ({
      initialValue: selectedValue,
    }),
    [selectedValue],
  );

  useEffect(
    () => {
      // when no quote, clear selected option in UI when no quote but still have previous value
      if (
        !quote &&
        !!previousVehicleTypeInForm &&
        previousVehicleTypeInForm !== NO_VEHICLE_SELECTED_VALUE &&
        selectedValue === NO_VEHICLE_SELECTED_VALUE
      ) {
        form.setFieldsValue({
          [deliveryFormFields.vehicleType.fieldName]: NO_VEHICLE_SELECTED_VALUE,
        });
        return;
      }
      if (quote && previousVehicleTypeInForm !== selectedValue) {
        form.setFieldsValue({
          [deliveryFormFields.vehicleType.fieldName]: selectedValue,
        });
      }
    },
    [form, quote, previousVehicleTypeInForm, selectedValue],
  );

  return (
    <SelectField
      id={deliveryFormFields.vehicleType.fieldName}
      fieldOptions={fieldOptions}
      placeholder={I18n.t(
        'v2.anytimeBooking.deliveryForm.vehicleTypePlaceholder',
      )}
      fieldClassName={styles.vehicleTypeField}
      className={styles.vehicleTypeSelect}
      form={form}
      options={options}
      disabled={disabled}
    />
  );
};

VehicleTypeField.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  form: PropTypes.shape({}).isRequired,
  disabled: PropTypes.bool,
};

VehicleTypeField.defaultProps = {
  disabled: false,
};

const ServiceConfigurations = ({
  form,
  deliveryIndex,
  delivery,
  dispatch,
  disabled = false,
}) => {
  const detailOverlayHandler = useCallback(
    () => {
      if (disabled) return; // Don't open overlay if disabled
      dispatch(
        NewBookingDuck.creators.openDetailOverlay(
          delivery.id,
          newBookingOverlayType.serviceConfiguration,
        ),
      );
    },
    [dispatch, delivery.id, disabled],
  );

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {I18n.t('v2.homepage.manualOrder.heading.deliveryOptions')}
      </div>
      <Row
        type="flex"
        gutter={{ md: 16 }}
        className={styles.serviceConfigurationsRow}
      >
        <Col xs={24} md={12}>
          <ServiceConfigurationField
            delivery={delivery}
            onClick={detailOverlayHandler}
            stateName={SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB}
            isFirstDelivery={deliveryIndex === 0}
            disabled={disabled}
          />
          <Icon type="right" className={styles.rightArrow} />
        </Col>
        <Col xs={24} md={8}>
          <VehicleTypeField
            form={form}
            delivery={delivery}
            disabled={disabled}
          />
        </Col>
        <TaxiTypeTnc delivery={delivery} className={styles.taxiTypeTnc} />
      </Row>
    </div>
  );
};

ServiceConfigurations.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  form: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  deliveryIndex: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
};

ServiceConfigurations.defaultProps = {
  disabled: false,
};

export default ServiceConfigurations;
